// =====================================================
// LIBRARY: Timeline Logger
// Description: Helpers pour logging automatique dans la timeline
// =====================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { EntityType } from '../types/timeline';
import { TIMELINE_EVENT_TYPE } from '../types/timeline';
import {
  ENTITY_LOGGING_CONFIGS,
  type EntityLoggingConfig,
} from '../config/logging/entity-logging.config';

/**
 * Récupérer le nom d'affichage d'une entité pour les logs de timeline
 * Permet d'avoir un contexte clair dans la GlobalTimeline
 */
export async function getEntityDisplayName(
  supabase: SupabaseClient,
  entityType: EntityType,
  entityId: string
): Promise<string | null> {
  try {
    switch (entityType) {
      case 'crew': {
        const { data } = await supabase.from('crews').select('name').eq('id', entityId).single();
        return data?.name || null;
      }
      case 'contact': {
        const { data } = await supabase
          .from('contacts')
          .select('first_name, last_name')
          .eq('id', entityId)
          .single();
        return data ? `${data.first_name} ${data.last_name}` : null;
      }
      case 'prospect': {
        const { data } = await supabase
          .from('form_submissions')
          .select('contact:contacts(first_name, last_name)')
          .eq('id', entityId)
          .single();
        const contact = data?.contact as any;
        return contact ? `${contact.first_name} ${contact.last_name}` : null;
      }
      default:
        return null;
    }
  } catch (error) {
    console.error(`Error fetching entity name for ${entityType}:`, error);
    return null;
  }
}

/**
 * Logger un changement de statut dans la timeline
 */
export async function logStatusChange(
  supabase: SupabaseClient,
  entityType: EntityType,
  entityId: string,
  fieldName: string,
  oldValue: string,
  newValue: string,
  entityName?: string | null,
  userId?: string | null
) {
  try {
    // Inclure le nom de l'entité dans le titre pour le contexte dans l'historique global
    const title = entityName ? `${entityName} - ${fieldName} modifié` : `${fieldName} modifié`;

    await supabase.from('timeline_events').insert({
      entity_type: entityType,
      entity_id: entityId,
      event_type: TIMELINE_EVENT_TYPE.STATUS_CHANGED,
      title,
      description: `${oldValue} → ${newValue}`,
      created_by: userId || null,
      metadata: {
        field: fieldName,
        old_value: oldValue,
        new_value: newValue,
        entity_name: entityName,
      },
    });
  } catch (error) {
    console.error('Error logging status change:', error);
  }
}

/**
 * Logger un changement d'assignation dans la timeline
 */
export async function logAssignedUserChange(
  supabase: SupabaseClient,
  entityType: EntityType,
  entityId: string,
  oldUserId: string | null,
  oldUserName: string | null,
  newUserId: string | null,
  newUserName: string | null,
  userId?: string | null
) {
  try {
    // Construire le titre et la description
    let title = 'Assignation modifiée';
    let description = '';

    if (!oldUserId && newUserId) {
      title = 'Assigné';
      description = `Assigné à ${newUserName || 'utilisateur'}`;
    } else if (oldUserId && !newUserId) {
      title = 'Assignation retirée';
      description = `Assignation de ${oldUserName || 'utilisateur'} retirée`;
    } else if (oldUserId && newUserId) {
      title = 'Assignation modifiée';
      description = `${oldUserName || 'utilisateur'} → ${newUserName || 'utilisateur'}`;
    }

    await supabase.from('timeline_events').insert({
      entity_type: entityType,
      entity_id: entityId,
      event_type: TIMELINE_EVENT_TYPE.ASSIGNED_USER_CHANGED,
      title,
      description,
      created_by: userId || null,
      metadata: {
        old_user_id: oldUserId,
        old_user_name: oldUserName,
        new_user_id: newUserId,
        new_user_name: newUserName,
      },
    });
  } catch (error) {
    console.error('Error logging assigned user change:', error);
  }
}

/**
 * Logger une mise à jour de données dans la timeline
 */
export async function logDataUpdate(
  supabase: SupabaseClient,
  entityType: EntityType,
  entityId: string,
  changes: Array<{ field: string; oldValue: any; newValue: any }>,
  entityName?: string | null,
  userId?: string | null
) {
  try {
    const summary = changes.map((c) => `${c.field}: ${c.oldValue} → ${c.newValue}`).join(', ');

    // Inclure le nom de l'entité dans le titre pour le contexte dans l'historique global
    const title = entityName ? `${entityName} - Données modifiées` : 'Données modifiées';

    await supabase.from('timeline_events').insert({
      entity_type: entityType,
      entity_id: entityId,
      event_type: TIMELINE_EVENT_TYPE.DATA_UPDATED,
      title,
      description: summary,
      created_by: userId || null,
      metadata: {
        fields_changed: changes,
        summary,
        entity_name: entityName,
      },
    });
  } catch (error) {
    console.error('Error logging data update:', error);
  }
}

/**
 * Normaliser une valeur (traiter null, undefined, '' comme équivalents)
 */
function normalizeValue(value: any): any {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return value;
}

/**
 * Vérifier si deux valeurs sont équivalentes après normalisation
 */
function areValuesEqual(oldValue: any, newValue: any): boolean {
  const normalizedOld = normalizeValue(oldValue);
  const normalizedNew = normalizeValue(newValue);
  return normalizedOld === normalizedNew;
}

/**
 * Détecter les changements de champs entre oldData et newData
 * Retourne les changements classés par type (status vs data)
 *
 * @param oldData - Données avant modification
 * @param newData - Données après modification
 * @param config - Configuration de l'entité
 * @param changedFields - Liste des champs qui ont réellement changé (optionnel)
 */
function detectFieldChanges(
  oldData: any,
  newData: any,
  config: EntityLoggingConfig,
  changedFields?: string[]
): {
  statusChanges: Array<{ field: string; oldValue: any; newValue: any }>;
  dataChanges: Array<{ field: string; oldValue: any; newValue: any }>;
} {
  const statusChanges: Array<{ field: string; oldValue: any; newValue: any }> = [];
  const dataChanges: Array<{ field: string; oldValue: any; newValue: any }> = [];

  for (const [fieldKey, fieldConfig] of Object.entries(config.trackedFields)) {
    // Si on a une liste de champs modifiés, ne vérifier que ceux-là
    if (changedFields && !changedFields.includes(fieldKey)) {
      continue;
    }

    const oldValue = oldData[fieldKey];
    const newValue = newData[fieldKey];

    // Ignorer si pas de changement (avec normalisation)
    if (areValuesEqual(oldValue, newValue)) continue;

    // Formater les valeurs si nécessaire
    const formattedOldValue = fieldConfig.formatValue
      ? fieldConfig.formatValue(oldValue, oldData)
      : oldValue;
    const formattedNewValue = fieldConfig.formatValue
      ? fieldConfig.formatValue(newValue, newData)
      : newValue;

    // Vérifier si les valeurs formatées sont identiques (ex: null et '' → "N/A")
    if (formattedOldValue === formattedNewValue) continue;

    const change = {
      field: fieldConfig.label,
      oldValue: formattedOldValue,
      newValue: formattedNewValue,
    };

    if (fieldConfig.isStatus) {
      statusChanges.push(change);
    } else {
      dataChanges.push(change);
    }
  }

  return { statusChanges, dataChanges };
}

/**
 * Logger l'ajout d'un membre à un équipage
 * Crée des événements dans la timeline du crew ET du contact
 */
export async function logCrewMemberAdded(
  supabase: SupabaseClient,
  crewId: string,
  crewName: string,
  contactId: string,
  contactName: string,
  userId?: string | null
) {
  try {
    // Préparer les données d'insert
    const baseCrewEvent = {
      entity_type: 'crew' as const,
      entity_id: crewId,
      event_type: TIMELINE_EVENT_TYPE.CREW_MEMBER_ADDED,
      title: 'Membre ajouté', // OK pour la timeline du crew spécifique
      description: `${contactName} ajouté à l'équipage`,
      metadata: {
        contact_id: contactId,
        contact_name: contactName,
        crew_name: crewName,
      },
    };

    const baseContactEvent = {
      entity_type: 'contact' as const,
      entity_id: contactId,
      event_type: TIMELINE_EVENT_TYPE.CREW_MEMBER_ADDED,
      title: `${contactName} - Ajouté à l'équipage "${crewName}"`, // ✅ Inclure le nom du contact pour GlobalTimeline
      description: `Ajouté à l'équipage ${crewName}`,
      metadata: {
        crew_id: crewId,
        crew_name: crewName,
        contact_name: contactName,
      },
    };

    const crewEvent = { ...baseCrewEvent, created_by: userId };
    const contactEvent = { ...baseContactEvent, created_by: userId };

    // Créer les deux événements en parallèle
    const [crewResult, contactResult] = await Promise.all([
      supabase.from('timeline_events').insert(crewEvent),
      supabase.from('timeline_events').insert(contactEvent),
    ]);

    // Vérifier les erreurs et logger plus de détails
    if (crewResult.error) {
      console.error('Error logging crew member added (crew timeline):', crewResult.error);
      // Si c'est une erreur de foreign key, suggérer la déconnexion
      if (crewResult.error.code === '23503') {
        console.warn(
          '⚠️ Session invalide détectée. Veuillez vous déconnecter et vous reconnecter.'
        );
      }
    }
    if (contactResult.error) {
      console.error('Error logging crew member added (contact timeline):', contactResult.error);
      if (contactResult.error.code === '23503') {
        console.warn(
          '⚠️ Session invalide détectée. Veuillez vous déconnecter et vous reconnecter.'
        );
      }
    }
  } catch (error) {
    console.error('Error logging crew member added:', error);
  }
}

/**
 * Logger le retrait d'un membre d'un équipage
 * Crée des événements dans la timeline du crew ET du contact
 */
export async function logCrewMemberRemoved(
  supabase: SupabaseClient,
  crewId: string,
  crewName: string,
  contactId: string,
  contactName: string,
  userId?: string | null
) {
  try {
    // Préparer les données d'insert (sans created_by si user invalide)
    const baseCrewEvent = {
      entity_type: 'crew' as const,
      entity_id: crewId,
      event_type: TIMELINE_EVENT_TYPE.CREW_MEMBER_REMOVED,
      title: 'Membre retiré', // OK pour la timeline du crew spécifique
      description: `${contactName} retiré de l'équipage`,
      metadata: {
        contact_id: contactId,
        contact_name: contactName,
        crew_name: crewName,
      },
    };

    const baseContactEvent = {
      entity_type: 'contact' as const,
      entity_id: contactId,
      event_type: TIMELINE_EVENT_TYPE.CREW_MEMBER_REMOVED,
      title: `${contactName} - Retiré de l'équipage "${crewName}"`, // ✅ Inclure le nom du contact pour GlobalTimeline
      description: `Retiré de l'équipage ${crewName}`,
      metadata: {
        crew_id: crewId,
        crew_name: crewName,
        contact_name: contactName,
      },
    };

    const crewEvent = { ...baseCrewEvent, created_by: userId };
    const contactEvent = { ...baseContactEvent, created_by: userId };

    // Créer les deux événements en parallèle
    const [crewResult, contactResult] = await Promise.all([
      supabase.from('timeline_events').insert(crewEvent),
      supabase.from('timeline_events').insert(contactEvent),
    ]);

    // Vérifier les erreurs
    if (crewResult.error) {
      console.error('Error logging crew member removed (crew timeline):', crewResult.error);
      if (crewResult.error.code === '23503') {
        console.warn(
          '⚠️ Session invalide détectée. Veuillez vous déconnecter et vous reconnecter.'
        );
      }
    }
    if (contactResult.error) {
      console.error('Error logging crew member removed (contact timeline):', contactResult.error);
      if (contactResult.error.code === '23503') {
        console.warn(
          '⚠️ Session invalide détectée. Veuillez vous déconnecter et vous reconnecter.'
        );
      }
    }
  } catch (error) {
    console.error('Error logging crew member removed:', error);
  }
}

/**
 * Récupérer une entité avec toutes ses relations pour le logging
 * Permet d'avoir les noms au lieu des UUIDs dans les logs
 */
async function fetchEntityWithRelations(
  supabase: SupabaseClient,
  entityType: EntityType,
  entityId: string
): Promise<any | null> {
  try {
    let query;

    switch (entityType) {
      case 'crew':
        query = supabase
          .from('crews')
          .select(
            `
            *,
            product:products(id, name),
            vehicle:vehicles(id, registration),
            association:associations(id, name),
            discount:discounts(id, name, amount),
            members:crew_members(contact:contacts(id, first_name, last_name))
          `
          )
          .eq('id', entityId)
          .single();
        break;

      case 'contact':
        query = supabase
          .from('contacts')
          .select(
            `
            *,
            origin:origins(id, label),
            products:contact_products(product:products(id, name))
          `
          )
          .eq('id', entityId)
          .single();
        break;

      case 'prospect':
        query = supabase
          .from('form_submissions')
          .select(
            `
            *,
            contact:contacts(id, first_name, last_name),
            form:forms(id, name)
          `
          )
          .eq('id', entityId)
          .single();
        break;

      default:
        return null;
    }

    const { data, error } = await query;

    if (error) {
      console.error(`Error fetching ${entityType} with relations:`, error);
      return null;
    }

    // Transformer la structure pour contact (products via relation many-to-many)
    if (entityType === 'contact' && data) {
      return {
        ...data,
        products: data.products?.map((cp: any) => cp.product).filter(Boolean) || [],
      };
    }

    // Transformer la structure pour crew (members via relation many-to-many)
    if (entityType === 'crew' && data) {
      return {
        ...data,
        members: data.members?.map((cm: any) => cm.contact).filter(Boolean) || [],
      };
    }

    return data;
  } catch (error) {
    console.error(`Error in fetchEntityWithRelations for ${entityType}:`, error);
    return null;
  }
}

/**
 * Logger automatiquement les changements d'une entité
 * Compare oldData et newData, détecte les changements métier, groupe les logs
 *
 * @param supabase - Client Supabase
 * @param entityType - Type d'entité (crew, contact, prospect)
 * @param entityId - ID de l'entité
 * @param oldData - Données avant modification
 * @param newData - Données après modification (merge de oldData + input)
 * @param changedFields - Clés des champs qui ont réellement changé (optionnel, déduit de input)
 * @param userId - ID de l'utilisateur effectuant le changement
 */
export async function logEntityUpdate(
  supabase: SupabaseClient,
  entityType: EntityType,
  entityId: string,
  oldData: any,
  newData: any,
  userId?: string | null,
  changedFields?: string[]
): Promise<void> {
  try {
    // Récupérer la configuration de logging pour cette entité
    const config = ENTITY_LOGGING_CONFIGS[entityType];
    if (!config) {
      // Pas de config = pas de logging automatique
      return;
    }

    // 🔥 AMÉLIORATION: Récupérer les données complètes avec toutes les relations
    // pour avoir les noms au lieu des UUIDs dans les logs
    const actualNewData = await fetchEntityWithRelations(supabase, entityType, entityId);

    // Si la récupération échoue, utiliser les données mergées comme fallback
    const dataToLog = actualNewData || newData;

    if (!actualNewData) {
      console.warn(
        `[logEntityUpdate] Could not fetch ${entityType} with relations, using merged data as fallback`
      );
    }

    // 1. Détecter les changements métier (ex: équipage inscrit)
    if (config.statusDetectors && config.statusDetectors.length > 0) {
      // Trier par priorité décroissante
      const sortedDetectors = [...config.statusDetectors].sort((a, b) => b.priority - a.priority);

      // Trouver le premier détecteur qui correspond
      const statusChange = sortedDetectors.find((detector) => detector.detect(oldData, dataToLog));

      if (statusChange) {
        // Log groupé pour changement métier
        const { error } = await supabase.from('timeline_events').insert({
          entity_type: entityType,
          entity_id: entityId,
          event_type: TIMELINE_EVENT_TYPE.STATUS_CHANGED,
          title: statusChange.name,
          description: statusChange.description(oldData, dataToLog),
          created_by: userId || null,
          metadata: {
            detector_name: statusChange.name,
            detector_priority: statusChange.priority,
          },
        });

        if (error) {
          console.error('Error inserting timeline event:', error);
        }

        // Ne pas logger les champs individuels si on a un changement métier détecté
        return;
      }
    }

    // 2. Logger les changements de champs importants
    const changes = detectFieldChanges(oldData, dataToLog, config, changedFields);

    // 3. Extraire le nom de l'entité pour le contexte dans l'historique global
    let entityName: string | null = null;
    switch (entityType) {
      case 'crew':
        entityName = dataToLog.name || null;
        break;
      case 'contact':
        entityName =
          dataToLog.first_name && dataToLog.last_name
            ? `${dataToLog.first_name} ${dataToLog.last_name}`
            : null;
        break;
      case 'prospect':
        // Pour les prospects, utiliser le nom du contact associé si disponible
        if (dataToLog.contact) {
          entityName = `${dataToLog.contact.first_name} ${dataToLog.contact.last_name}`;
        }
        break;
    }

    // Logger les changements de statut séparément
    if (changes.statusChanges.length > 0) {
      for (const change of changes.statusChanges) {
        await logStatusChange(
          supabase,
          entityType,
          entityId,
          change.field,
          change.oldValue,
          change.newValue,
          entityName,
          userId
        );
      }
    }

    // Logger les autres changements groupés
    if (changes.dataChanges.length > 0) {
      await logDataUpdate(supabase, entityType, entityId, changes.dataChanges, entityName, userId);
    }
  } catch (error) {
    console.error('Error logging entity update:', error);
    // Ne pas faire échouer l'update si le logging échoue
  }
}
